import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GatePass from './GatePass';
import './VisitorsManagement.css';

function VisitorsManagement() {
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPassVisitor, setSelectedPassVisitor] = useState(null);

  // Pagination (10 entries per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filters
  const [filters, setFilters] = useState({
    searchQuery: '',
    startDate: '',
    endDate: '',
    purposeOfVisit: '',
    status: 'all', // all, checkedIn, checkedOut
    passStatus: 'all', // all, active, inactive
  });

  const [sortBy, setSortBy] = useState('visitDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch all visitors
  const fetchVisitors = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/visitors?limit=all');
      setVisitors(response.data.data);
      applyFilters(response.data.data);
    } catch (err) {
      setError('Failed to fetch visitors. Make sure the server is running.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and sorting
  const applyFilters = (data) => {
    let filtered = [...data];

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.name.toLowerCase().includes(query) ||
          (v.email && v.email.toLowerCase().includes(query)) ||
          (v.phone && v.phone.includes(query)) ||
          (v.aadharNumber && v.aadharNumber.includes(query)) ||
          (v.whomToMeet && v.whomToMeet.toLowerCase().includes(query))
      );
    }

    // Date range filter
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((v) => new Date(v.visitDate) >= startDate);
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((v) => new Date(v.visitDate) <= endDate);
    }

    // Purpose filter
    if (filters.purposeOfVisit) {
      filtered = filtered.filter((v) => v.purposeOfVisit === filters.purposeOfVisit);
    }

    // Status filter
    if (filters.status === 'checkedIn') {
      filtered = filtered.filter((v) => !v.checkOutTime);
    } else if (filters.status === 'checkedOut') {
      filtered = filtered.filter((v) => v.checkOutTime);
    }

    // Pass Status filter
    if (filters.passStatus === 'active') {
      filtered = filtered.filter((v) => v.passStatus !== 'inactive');
    } else if (filters.passStatus === 'inactive') {
      filtered = filtered.filter((v) => v.passStatus === 'inactive');
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (sortBy === 'visitDate') {
        aValue = new Date(a.visitDate).getTime();
        bValue = new Date(b.visitDate).getTime();
      } else if (sortBy === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else if (sortBy === 'company') {
        aValue = (a.company || '').toLowerCase();
        bValue = (b.company || '').toLowerCase();
      } else if (sortBy === 'whomToMeet') {
        aValue = (a.whomToMeet || '').toLowerCase();
        bValue = (b.whomToMeet || '').toLowerCase();
      } else if (sortBy === 'purpose') {
        aValue = a.purposeOfVisit.toLowerCase();
        bValue = b.purposeOfVisit.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    setFilteredVisitors(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchVisitors();
  }, []);

  useEffect(() => {
    applyFilters(visitors);
  }, [filters, sortBy, sortOrder]);

  // Date Helpers
  const toLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateRangeForPreset = (preset) => {
    const now = new Date();
    const todayStr = toLocalDateString(now);

    if (preset === 'today') {
      return { startDate: todayStr, endDate: todayStr };
    } else if (preset === 'last7') {
      const past = new Date();
      past.setDate(now.getDate() - 7);
      return { startDate: toLocalDateString(past), endDate: todayStr };
    } else if (preset === 'lastMonth') {
      const past = new Date();
      past.setMonth(now.getMonth() - 1);
      return { startDate: toLocalDateString(past), endDate: todayStr };
    } else if (preset === 'thisMonth') {
      const past = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: toLocalDateString(past), endDate: todayStr };
    }
    return { startDate: '', endDate: '' };
  };

  // State for active date presets and export modal
  const [activeDatePreset, setActiveDatePreset] = useState('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportPreset, setExportPreset] = useState('lastMonth');

  // Set filter preset in main view
  const handleApplyDatePreset = (preset) => {
    setActiveDatePreset(preset);
    const { startDate, endDate } = getDateRangeForPreset(preset);
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  // Pagination
  const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVisitors = filteredVisitors.slice(startIndex, startIndex + itemsPerPage);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDate' || name === 'endDate') {
      setActiveDatePreset('custom');
    }
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Handle delete (temporarily disabled on UI)
  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this visitor record?')) {
      try {
        await axios.delete(`/api/visitors/${id}`);
        setVisitors(visitors.filter((v) => v._id !== id));
        setSuccess('Visitor deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete visitor.');
        console.error(err);
      }
    }
  };

  // Handle checkout
  const handleCheckout = async (id) => {
    const now = new Date();
    const nowIso = now.toISOString();

    // 1. Instant optimistic update so button turns red IMMEDIATELY on click
    setVisitors((prev) =>
      prev.map((v) => (v._id === id ? { ...v, checkOutTime: nowIso } : v))
    );
    setFilteredVisitors((prev) =>
      prev.map((v) => (v._id === id ? { ...v, checkOutTime: nowIso } : v))
    );

    if (selectedPassVisitor && selectedPassVisitor._id === id) {
      setSelectedPassVisitor((prev) => ({
        ...prev,
        checkOutTime: nowIso,
      }));
    }

    try {
      const response = await axios.put(`/api/visitors/${id}`, {
        checkOutTime: now,
      });

      // Sync with server returned data
      setVisitors((prev) =>
        prev.map((v) => (v._id === id ? response.data : v))
      );
      setFilteredVisitors((prev) =>
        prev.map((v) => (v._id === id ? response.data : v))
      );

      if (selectedPassVisitor && selectedPassVisitor._id === id) {
        setSelectedPassVisitor(response.data);
      }

      setSuccess('Check-out recorded successfully! Marked as Checked Out.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to record check-out.');
      console.error(err);
      fetchVisitors();
    }
  };

  // Handle pass active / inactive toggle
  const handleTogglePassStatus = async (id, newStatus) => {
    try {
      const targetVisitor =
        visitors.find((v) => v._id === id) ||
        (selectedPassVisitor && selectedPassVisitor._id === id ? selectedPassVisitor : null);

      // If pass is already inactive, strictly disallow reactivating
      if (targetVisitor && targetVisitor.passStatus === 'inactive') {
        alert('This gate pass has already been deactivated. Once deactivated, a gate pass cannot be reactivated.');
        return;
      }

      let shouldCheckOut = false;

      // If deactivating pass and visitor has not checked out yet, ask to check out first
      if (newStatus === 'inactive' && targetVisitor && !targetVisitor.checkOutTime) {
        const confirmCheckOut = window.confirm(
          `Visitor "${targetVisitor.name}" has not checked out yet.\n\nTo make this pass INACTIVE, the visitor must be checked out first.\n\nWould you like to check out this visitor now and deactivate the pass?`
        );

        if (!confirmCheckOut) {
          // Cancelled: pass remains active
          return;
        }

        shouldCheckOut = true;
      }

      const now = new Date();
      const payload = { passStatus: newStatus };
      if (shouldCheckOut) {
        payload.checkOutTime = now;
      }

      // Optimistic instant update so UI reflects the change immediately
      const optimisticallyUpdated = visitors.map((v) =>
        v._id === id
          ? {
            ...v,
            passStatus: newStatus,
            ...(shouldCheckOut ? { checkOutTime: now.toISOString() } : {}),
          }
          : v
      );
      setVisitors(optimisticallyUpdated);
      applyFilters(optimisticallyUpdated);

      if (selectedPassVisitor && selectedPassVisitor._id === id) {
        setSelectedPassVisitor((prev) => ({
          ...prev,
          passStatus: newStatus,
          ...(shouldCheckOut ? { checkOutTime: now.toISOString() } : {}),
        }));
      }

      const response = await axios.put(`/api/visitors/${id}`, payload);

      const updated = visitors.map((v) => (v._id === id ? response.data : v));
      setVisitors(updated);
      applyFilters(updated);
      if (selectedPassVisitor && selectedPassVisitor._id === id) {
        setSelectedPassVisitor(response.data);
      }

      if (shouldCheckOut) {
        setSuccess(`Visitor checked out and pass deactivated successfully!`);
      } else {
        setSuccess(`Pass successfully set to ${newStatus.toUpperCase()}!`);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to update pass status.';
      setError(errMsg);
      console.error(err);
      fetchVisitors();
    }
  };

  // Format date
  const formatDate = (date) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      searchQuery: '',
      startDate: '',
      endDate: '',
      purposeOfVisit: '',
      status: 'all',
      passStatus: 'all',
    });
    setActiveDatePreset('all');
    setSortBy('visitDate');
    setSortOrder('desc');
  };

  // CSV Export logic
  const exportToCSV = (recordsToExport, filename = 'visitors_export') => {
    if (!recordsToExport || recordsToExport.length === 0) {
      setError('No visitor records found to export for the selected criteria.');
      setTimeout(() => setError(''), 3500);
      return;
    }

    const headers = [
      'Pass Number',
      'Visitor Name',
      'Aadhaar Number',
      'Email',
      'Phone',
      'Company',
      'Whom To Meet',
      'Purpose of Visit',
      'Check-In Time',
      'Check-Out Time',
      'Status',
      'Pass Status',
      'Notes',
      'Photo S3 URL',
      'ID Proof S3 URL',
    ];

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const rows = recordsToExport.map((v) => {
      const checkIn = v.visitDate ? new Date(v.visitDate).toLocaleString() : '';
      const checkOut = v.checkOutTime ? new Date(v.checkOutTime).toLocaleString() : '';
      const status = v.checkOutTime ? 'Checked Out' : 'Checked In';
      const passStatus = v.passStatus === 'inactive' ? 'Inactive' : 'Active';
      const passNo = v.passNumber || `GP-${(v._id || '').slice(-4).toUpperCase()}`;

      return [
        escapeCsv(passNo),
        escapeCsv(v.name),
        escapeCsv(v.aadharNumber || ''),
        escapeCsv(v.email || ''),
        escapeCsv(v.phone || ''),
        escapeCsv(v.company || ''),
        escapeCsv(v.whomToMeet || ''),
        escapeCsv(v.purposeOfVisit),
        escapeCsv(checkIn),
        escapeCsv(checkOut),
        escapeCsv(status),
        escapeCsv(passStatus),
        escapeCsv(v.notes || ''),
        escapeCsv(v.photoUrl || ''),
        escapeCsv(v.idProofUrl || ''),
      ].join(',');
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccess(`Exported ${recordsToExport.length} visitor record(s) to CSV!`);
    setTimeout(() => setSuccess(''), 3500);
  };

  const handleQuickExport = () => {
    const filename = `visitors_filtered_${filters.startDate || 'all'}_to_${filters.endDate || 'all'}`;
    exportToCSV(filteredVisitors, filename);
  };

  const handleOpenExportModal = () => {
    if (filters.startDate || filters.endDate) {
      setExportStartDate(filters.startDate);
      setExportEndDate(filters.endDate);
      setExportPreset(activeDatePreset === 'all' ? 'custom' : activeDatePreset);
    } else {
      handleSelectExportPreset('lastMonth');
    }
    setShowExportModal(true);
  };

  const handleSelectExportPreset = (preset) => {
    setExportPreset(preset);
    const { startDate, endDate } = getDateRangeForPreset(preset);
    setExportStartDate(startDate);
    setExportEndDate(endDate);
  };

  const getModalFilteredVisitors = () => {
    return visitors.filter((v) => {
      const vDate = new Date(v.visitDate);
      if (exportStartDate) {
        const start = new Date(exportStartDate);
        start.setHours(0, 0, 0, 0);
        if (vDate < start) return false;
      }
      if (exportEndDate) {
        const end = new Date(exportEndDate);
        end.setHours(23, 59, 59, 999);
        if (vDate > end) return false;
      }
      return true;
    });
  };

  const handleModalExport = () => {
    const modalRecords = getModalFilteredVisitors();
    const filename = `visitors_export_${exportStartDate || 'start'}_to_${exportEndDate || 'present'}`;
    exportToCSV(modalRecords, filename);
    setShowExportModal(false);
  };

  const modalExportRecords = getModalFilteredVisitors();

  if (loading) {
    return <div className="loading-container">Loading visitors...</div>;
  }

  return (
    <div className="visitors-management">
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="management-header">
        <div className="management-header-left">
          <h2>Visitor Records Management</h2>
          <p>Total Visitors: <strong>{visitors.length}</strong> | Filtered: <strong>{filteredVisitors.length}</strong></p>
        </div>
        <div className="management-header-actions">
          <button className="btn-export-main" onClick={handleOpenExportModal}>
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header-row">
          <h3>Filters & Search</h3>
          {/* Quick Date Presets */}
          <div className="date-presets-bar">
            <span className="presets-label">Date Range:</span>
            <button
              type="button"
              className={`preset-chip ${activeDatePreset === 'lastMonth' ? 'active' : ''}`}
              onClick={() => handleApplyDatePreset('lastMonth')}
            >
              Last 1 Month
            </button>
            <button
              type="button"
              className={`preset-chip ${activeDatePreset === 'thisMonth' ? 'active' : ''}`}
              onClick={() => handleApplyDatePreset('thisMonth')}
            >
              This Month
            </button>
            <button
              type="button"
              className={`preset-chip ${activeDatePreset === 'last7' ? 'active' : ''}`}
              onClick={() => handleApplyDatePreset('last7')}
            >
              Last 7 Days
            </button>
            <button
              type="button"
              className={`preset-chip ${activeDatePreset === 'today' ? 'active' : ''}`}
              onClick={() => handleApplyDatePreset('today')}
            >
              Today
            </button>
            <button
              type="button"
              className={`preset-chip ${activeDatePreset === 'all' ? 'active' : ''}`}
              onClick={() => handleApplyDatePreset('all')}
            >
              All Records
            </button>
          </div>
        </div>

        <div className="filter-grid">
          {/* Search */}
          <div className="filter-group">
            <label>Search (Name, Aadhaar, Email, Phone)</label>
            <input
              type="text"
              name="searchQuery"
              value={filters.searchQuery}
              onChange={handleFilterChange}
              placeholder="Search by name, Aadhaar, phone..."
              className="filter-input"
            />
          </div>

          {/* Date Range */}
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          {/* Purpose */}
          <div className="filter-group">
            <label>Purpose of Visit</label>
            <select
              name="purposeOfVisit"
              value={filters.purposeOfVisit}
              onChange={handleFilterChange}
              className="filter-input"
            >
              <option value="">All Purposes</option>
              <option value="Meeting">Meeting</option>
              <option value="Interview">Interview</option>
              <option value="Delivery">Delivery</option>
              <option value="Support">Support</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Status */}
          <div className="filter-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="filter-input"
            >
              <option value="all">All Visitors</option>
              <option value="checkedIn">Checked In</option>
              <option value="checkedOut">Checked Out</option>
            </select>
          </div>

          {/* Pass Status (Active / Inactive) Filter */}
          <div className="filter-group">
            <label>Pass Status</label>
            <select
              name="passStatus"
              value={filters.passStatus}
              onChange={handleFilterChange}
              className="filter-input"
            >
              <option value="all">All Passes</option>
              <option value="active">Active Passes Only</option>
              <option value="inactive">Inactive Passes Only</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="filter-group">
            <label>Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-input"
            >
              <option value="visitDate">Visit Date</option>
              <option value="name">Name</option>
              <option value="company">Company</option>
              <option value="whomToMeet">Whom to Meet</option>
              <option value="purpose">Purpose</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="filter-group">
            <label>Order</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="filter-input"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        <div className="filter-actions-row">
          <button className="btn-reset" onClick={resetFilters}>
            ↺ Reset Filters
          </button>
          <button
            className="btn-quick-export"
            onClick={handleQuickExport}
            title="Download CSV of current filtered visitors"
          >
            📥 Export Filtered ({filteredVisitors.length})
          </button>
        </div>
      </div>

      {/* Results */}
      {paginatedVisitors.length === 0 ? (
        <div className="no-results">
          <p>No visitors found matching your filters.</p>
        </div>
      ) : (
        <>
          <div className="visitors-table-container">
            <table className="visitors-table">
              <thead>
                <tr>
                  <th>Visitor & ID</th>
                  <th>Pass Number</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Company</th>
                  <th>Whom to Meet</th>
                  <th>Purpose</th>
                  <th>Check-In</th>
                  <th>Pass Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVisitors.map((visitor) => {
                  const isPassInactive = visitor.passStatus === 'inactive';
                  const passNo =
                    visitor.passNumber ||
                    `GP-${(visitor._id || '').slice(-4).toUpperCase()}`;

                  return (
                    <tr key={visitor._id} className="visitor-row">
                      <td className="visitor-name">
                        <div className="visitor-name-flex">
                          {visitor.photoUrl ? (
                            <img
                              src={`/api/visitors/${visitor._id}/photo`}
                              alt={visitor.name}
                              className="table-visitor-avatar-img"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const fallback = e.target.parentElement.querySelector('.table-visitor-avatar-initials');
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <span
                            className="table-visitor-avatar-initials"
                            style={{ display: visitor.photoUrl ? 'none' : 'flex' }}
                          >
                            {(visitor.name || 'V').slice(0, 1).toUpperCase()}
                          </span>
                          <div className="visitor-name-info">
                            <div className="visitor-primary-name">{visitor.name}</div>
                            <div className="user-subid">
                              ID: {visitor._id ? visitor._id.slice(-6).toUpperCase() : 'N/A'}
                            </div>
                            {visitor.aadharNumber && (
                              <div className="user-subid" style={{ color: '#0284c7', fontWeight: 600 }}>
                                🆔 {visitor.aadharNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="pass-code-pill">{passNo}</span>
                      </td>
                      <td className="visitor-email">{visitor.email || '—'}</td>
                      <td>{visitor.phone || '—'}</td>
                      <td>{visitor.company || 'N/A'}</td>
                      <td>{visitor.whomToMeet || '—'}</td>
                      <td>{visitor.purposeOfVisit}</td>
                      <td className="date-cell">{formatDate(visitor.visitDate)}</td>
                      <td>
                        <span
                          className={`status-badge ${isPassInactive ? 'pass-inactive' : 'pass-active'
                            }`}
                        >
                          {isPassInactive ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="btn-small btn-pass-small"
                          onClick={() => setSelectedPassVisitor(visitor)}
                          title="View & Print Gate Pass"
                        >
                          🪪 Pass
                        </button>
                        {/* {(visitor.idProofUrl || visitor.idProofKey) && (
                          <a
                            href={`/api/visitors/${visitor._id}/idproof`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-small btn-idproof-small"
                          title="View Uploaded ID Proof (opens in new tab)"
                          >
                            📄 ID Proof
                          </a>
                        )} */}
                        {isPassInactive ? (
                          <span
                            className="badge-deactivated-permanent"
                            title="This gate pass is permanently deactivated and cannot be reactivated"
                          >
                            🔒 Deactivated
                          </span>
                        ) : (
                          <button
                            className="btn-small btn-deactivate-small"
                            onClick={() => handleTogglePassStatus(visitor._id, 'inactive')}
                            title="Deactivate pass permanently"
                          >
                            🔴 Deactivate
                          </button>
                        )}
                        {!visitor.checkOutTime ? (
                          <button
                            className="btn-small btn-success-small"
                            onClick={() => handleCheckout(visitor._id)}
                            title="Check Out"
                          >
                            ✓ Check Out
                          </button>
                        ) : (
                          <button
                            className="btn-small btn-checked-out-red"
                            disabled
                            title={`Checked out on ${formatDate(visitor.checkOutTime)}`}
                          >
                            Checked Out
                          </button>
                        )}
                        {/* Delete functionality temporarily hidden */}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>

            <div className="pagination-info">
              Page {currentPage} of {totalPages} | Showing {paginatedVisitors.length} of{' '}
              {filteredVisitors.length} records
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </>
      )}

      {selectedPassVisitor && (
        <GatePass
          visitor={selectedPassVisitor}
          isModal={true}
          onClose={() => setSelectedPassVisitor(null)}
          onToggleStatus={handleTogglePassStatus}
        />
      )}

      {/* Export to CSV Modal */}
      {showExportModal && (
        <div
          className="export-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowExportModal(false)}
        >
          <div className="export-modal-card">
            <div className="export-modal-header">
              <div className="export-modal-title">
                <span className="export-icon">📊</span>
                <h3>Export Visitors to CSV</h3>
              </div>
              <button
                className="export-close-btn"
                onClick={() => setShowExportModal(false)}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="export-modal-body">
              <p className="export-instruction">
                Select a quick date preset or define custom <strong>From Date</strong> to <strong>To Date</strong> to generate your CSV export.
              </p>

              <div className="export-presets-grid">
                <button
                  type="button"
                  className={`export-preset-card ${exportPreset === 'lastMonth' ? 'active' : ''}`}
                  onClick={() => handleSelectExportPreset('lastMonth')}
                >
                  <span className="preset-card-title">📅 Last 1 Month</span>
                  <span className="preset-card-desc">Visitors from past 30 days</span>
                </button>
                <button
                  type="button"
                  className={`export-preset-card ${exportPreset === 'thisMonth' ? 'active' : ''}`}
                  onClick={() => handleSelectExportPreset('thisMonth')}
                >
                  <span className="preset-card-title">📅 This Month</span>
                  <span className="preset-card-desc">1st of month to today</span>
                </button>
                <button
                  type="button"
                  className={`export-preset-card ${exportPreset === 'last7' ? 'active' : ''}`}
                  onClick={() => handleSelectExportPreset('last7')}
                >
                  <span className="preset-card-title">📅 Last 7 Days</span>
                  <span className="preset-card-desc">Recent week activity</span>
                </button>
                <button
                  type="button"
                  className={`export-preset-card ${exportPreset === 'all' ? 'active' : ''}`}
                  onClick={() => handleSelectExportPreset('all')}
                >
                  <span className="preset-card-title">🌐 All Records</span>
                  <span className="preset-card-desc">Complete visitor history</span>
                </button>
              </div>

              <div className="export-dates-row">
                <div className="filter-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => {
                      setExportStartDate(e.target.value);
                      setExportPreset('custom');
                    }}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => {
                      setExportEndDate(e.target.value);
                      setExportPreset('custom');
                    }}
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="export-summary-box">
                <div className="summary-line">
                  <span>Selected Range:</span>
                  <strong>
                    {exportStartDate || 'Beginning of time'} → {exportEndDate || 'Present'}
                  </strong>
                </div>
                <div className="summary-line">
                  <span>Matching Records:</span>
                  <span className="count-highlight">{modalExportRecords.length} visitors</span>
                </div>
              </div>
            </div>

            <div className="export-modal-footer">
              <button className="btn-cancel" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              <button
                className="btn-download-csv"
                onClick={handleModalExport}
                disabled={modalExportRecords.length === 0}
              >
                ⬇️ Download CSV ({modalExportRecords.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisitorsManagement;
